import {
  COLOR_CHOICES,
  FILL_COLOR,
  LINE_WIDTH,
  NODE_RADIUS,
  ZONE_TYPES,
  LINE_CROSSING_COLORS,
  LINE_CROSSING_STEPS,
} from "./constants";

export function makeLine(ctx, x1, y1, x2, y2) {
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
}

export function drawNode(ctx, x, y, stroke = null) {
  if (stroke) {
    ctx.strokeStyle = stroke;
  }
  ctx.beginPath();
  ctx.arc(x, y, NODE_RADIUS, 0, 2 * Math.PI);
  ctx.closePath();
  ctx.fillStyle = "white";
  ctx.fill();
  ctx.stroke();
}

export function isClockwise(vertices) {
  let sum = 0;
  for (let i = 0; i < vertices.length; i++) {
    const [x1, y1] = vertices[i];
    const [x2, y2] = vertices[(i + 1) % vertices.length];
    sum += (x2 - x1) * (y2 + y1);
  }
  return sum > 0;
}

export function drawAllPolygons(
  ctx,
  img,
  masterPoints,
  masterColors,
  masterZoneTypes = [],
) {
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  ctx.drawImage(img, 0, 0, ctx.canvas.width, ctx.canvas.height);

  // Calculate scale factor from canvas vs image dimensions
  const scaleFactor = ctx.canvas.width / img.width;

  // Draw polygons as subpaths and fill all at once
  // This avoids overlapping polygons becoming opaque
  ctx.beginPath();
  ctx.fillStyle = FILL_COLOR;

  for (let i = 0; i < masterPoints.length; i++) {
    const points = masterPoints[i];
    const zoneType = masterZoneTypes[i];

    // Only fill polygon zones, not line zones
    if (
      points.length > 0 &&
      zoneType !== ZONE_TYPES.LINE_CROSSING &&
      zoneType !== ZONE_TYPES.DIRECTION_DETECTION
    ) {
      ctx.moveTo(points[0][0] * scaleFactor, points[0][1] * scaleFactor);
      for (let j = 1; j < points.length; j++) {
        ctx.lineTo(points[j][0] * scaleFactor, points[j][1] * scaleFactor);
      }
      ctx.closePath();
    }
  }
  ctx.fill();

  // Draw polygon outlines and nodes
  ctx.lineWidth = LINE_WIDTH;
  ctx.lineJoin = "bevel";

  for (let i = 0; i < masterPoints.length; i++) {
    const points = masterPoints[i];
    const color = masterColors[i];
    const zoneType = masterZoneTypes[i];

    if (points.length > 1) {
      if (zoneType === ZONE_TYPES.LINE_CROSSING && points.length === 8) {
        // Draw Line Crossing dual lines (scaledrawLineCrossingZone handles scaling)
        drawLineCrossingZone(ctx, points, color, scaleFactor);
      } else if (
        zoneType === ZONE_TYPES.DIRECTION_DETECTION ||
        zoneType === ZONE_TYPES.LINE_CROSSING
      ) {
        // Draw single line for direction detection or incomplete line crossing
        drawLineZone(ctx, points, color, scaleFactor);
      } else {
        // Draw polygon zone
        ctx.strokeStyle = color;
        ctx.beginPath();
        ctx.moveTo(points[0][0] * scaleFactor, points[0][1] * scaleFactor);
        for (let j = 1; j < points.length; j++) {
          ctx.lineTo(points[j][0] * scaleFactor, points[j][1] * scaleFactor);
        }
        // Close the polygon by drawing line back to first point
        ctx.lineTo(points[0][0] * scaleFactor, points[0][1] * scaleFactor);
        ctx.stroke();
      }
    }

    // Draw nodes for each point
    if (zoneType === ZONE_TYPES.LINE_CROSSING && points.length === 8) {
      // Draw nodes for both direction and crossing lines
      drawNode(
        ctx,
        points[0] * scaleFactor,
        points[1] * scaleFactor,
        LINE_CROSSING_COLORS[LINE_CROSSING_STEPS.DIRECTION],
      );
      drawNode(
        ctx,
        points[2] * scaleFactor,
        points[3] * scaleFactor,
        LINE_CROSSING_COLORS[LINE_CROSSING_STEPS.DIRECTION],
      );
      drawNode(
        ctx,
        points[4] * scaleFactor,
        points[5] * scaleFactor,
        LINE_CROSSING_COLORS[LINE_CROSSING_STEPS.CROSSING],
      );
      drawNode(
        ctx,
        points[6] * scaleFactor,
        points[7] * scaleFactor,
        LINE_CROSSING_COLORS[LINE_CROSSING_STEPS.CROSSING],
      );
    } else {
      for (let j = 0; j < points.length; j++) {
        drawNode(ctx, points[j][0] * scaleFactor, points[j][1] * scaleFactor, color);
      }
    }
  }
}

export function drawCurrentPolygon(ctx, points, color, img) {
  if (points.length === 0) return;

  // Calculate scale factor from canvas vs image dimensions
  const scaleFactor = img ? ctx.canvas.width / img.width : 1;

  ctx.lineWidth = LINE_WIDTH;
  ctx.lineJoin = "bevel";
  ctx.strokeStyle = color;

  // Draw lines between points
  for (let i = 0; i < points.length - 1; i++) {
    ctx.beginPath();
    makeLine(
      ctx,
      points[i][0] * scaleFactor,
      points[i][1] * scaleFactor,
      points[i + 1][0] * scaleFactor,
      points[i + 1][1] * scaleFactor,
    );
    ctx.stroke();
  }

  // Draw nodes with numbers
  for (let i = 0; i < points.length; i++) {
    drawNode(ctx, points[i][0] * scaleFactor, points[i][1] * scaleFactor, color);

    // Draw point number
    ctx.fillStyle = color;
    ctx.font = "bold 12px Arial";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(String(i + 1), points[i][0] * scaleFactor, points[i][1] * scaleFactor - 12);
  }
}

export function drawPreviewLine(
  ctx,
  points,
  currentX,
  currentY,
  color,
  drawMode,
  img,
) {
  if (points.length === 0) return;

  // Calculate scale factor from canvas vs image dimensions
  const scaleFactor = img ? ctx.canvas.width / img.width : 1;

  if (
    (points.length > 0 && drawMode === "polygon") ||
    (points.length > 0 && points.length < 2 && drawMode === "line")
  ) {
    ctx.beginPath();
    ctx.lineJoin = "bevel";
    ctx.strokeStyle = color;
    makeLine(
      ctx,
      points[points.length - 1][0] * scaleFactor,
      points[points.length - 1][1] * scaleFactor,
      currentX,
      currentY,
    );
    ctx.stroke();
  }
}

export function findClosestPoint(x, y, masterPoints, scaleFactor) {
  let minDist = Infinity;
  let closestPoint = null;
  let polygonIndex = -1;
  let pointIndex = -1;

  for (let i = 0; i < masterPoints.length; i++) {
    for (let j = 0; j < masterPoints[i].length; j++) {
      const [px, py] = masterPoints[i][j];
      const dist = Math.sqrt((x - px) ** 2 + (y - py) ** 2);
      if (dist < minDist && dist < 10 / scaleFactor) {
        minDist = dist;
        closestPoint = [px, py];
        polygonIndex = i;
        pointIndex = j;
      }
    }
  }

  return { point: closestPoint, polygonIndex, pointIndex };
}

export function calculateImageScale(
  img,
  containerWidth,
  containerHeight,
  maxScale = 0.95,
) {
  const maxWidth = containerWidth * maxScale;
  const maxHeight = containerHeight * maxScale;

  // Calculate scale factors for width and height
  const scaleX = maxWidth / img.width;
  const scaleY = maxHeight / img.height;

  // Use the smaller scale factor to ensure the image fits within the container
  // while maintaining aspect ratio
  return Math.min(scaleX, scaleY);
}

export function constrainToAngle(
  lastPoint,
  currentX,
  currentY,
  radiansPerAngle,
) {
  const dx = currentX - lastPoint[0];
  const dy = currentY - lastPoint[1];
  const angle = Math.atan2(dy, dx);
  const length = Math.sqrt(dx * dx + dy * dy);
  const snappedAngle = Math.round(angle / radiansPerAngle) * radiansPerAngle;
  const newX = lastPoint[0] + length * Math.cos(snappedAngle);
  const newY = lastPoint[1] + length * Math.sin(snappedAngle);
  return [Math.round(newX), Math.round(newY)];
}

export function isPointNearFirstPoint(x, y, firstPoint, threshold = 6.5) {
  const distX = x - firstPoint[0];
  const distY = y - firstPoint[1];
  return Math.sqrt(distX * distX + distY * distY) <= threshold;
}

export function drawLineCrossingZone(ctx, points, color, scaleFactor = 1) {
  if (points.length !== 8) return;

  ctx.lineWidth = LINE_WIDTH;
  ctx.lineJoin = "bevel";

  // Draw direction line (first 4 values: x1d, y1d, x2d, y2d)
  ctx.strokeStyle = LINE_CROSSING_COLORS[LINE_CROSSING_STEPS.DIRECTION];
  ctx.beginPath();
  makeLine(ctx, points[0] * scaleFactor, points[1] * scaleFactor, points[2] * scaleFactor, points[3] * scaleFactor);
  ctx.stroke();

  // Draw crossing line (last 4 values: x1c, y1c, x2c, y2c)
  ctx.strokeStyle = LINE_CROSSING_COLORS[LINE_CROSSING_STEPS.CROSSING];
  ctx.beginPath();
  makeLine(ctx, points[4] * scaleFactor, points[5] * scaleFactor, points[6] * scaleFactor, points[7] * scaleFactor);
  ctx.stroke();

  // Add labels
  ctx.fillStyle = LINE_CROSSING_COLORS[LINE_CROSSING_STEPS.DIRECTION];
  ctx.font = "12px Arial";
  ctx.fillText("Direction", points[0] * scaleFactor + 5, points[1] * scaleFactor - 5);

  ctx.fillStyle = LINE_CROSSING_COLORS[LINE_CROSSING_STEPS.CROSSING];
  ctx.fillText("Crossing", points[4] * scaleFactor + 5, points[5] * scaleFactor - 5);
}

export function drawLineZone(ctx, points, color, scaleFactor = 1) {
  if (points.length < 2) return;

  ctx.lineWidth = LINE_WIDTH;
  ctx.lineJoin = "bevel";
  ctx.strokeStyle = color;

  ctx.beginPath();
  makeLine(
    ctx,
    points[0][0] * scaleFactor,
    points[0][1] * scaleFactor,
    points[points.length - 1][0] * scaleFactor,
    points[points.length - 1][1] * scaleFactor,
  );
  ctx.stroke();
}

export function drawLineCrossingWorkInProgress(
  ctx,
  directionLine,
  crossingLine,
  lineCrossingStep,
  img,
) {
  // Calculate scale factor from canvas vs image dimensions
  const scaleFactor = img ? ctx.canvas.width / img.width : 1;

  ctx.lineWidth = LINE_WIDTH;
  ctx.lineJoin = "bevel";

  // Draw completed or in-progress direction line
  if (directionLine.length === 2) {
    ctx.strokeStyle = LINE_CROSSING_COLORS[LINE_CROSSING_STEPS.DIRECTION];
    ctx.beginPath();
    makeLine(
      ctx,
      directionLine[0][0] * scaleFactor,
      directionLine[0][1] * scaleFactor,
      directionLine[1][0] * scaleFactor,
      directionLine[1][1] * scaleFactor,
    );
    ctx.stroke();

    // Draw direction line nodes with numbers
    drawNode(
      ctx,
      directionLine[0][0] * scaleFactor,
      directionLine[0][1] * scaleFactor,
      LINE_CROSSING_COLORS[LINE_CROSSING_STEPS.DIRECTION],
    );
    drawNode(
      ctx,
      directionLine[1][0] * scaleFactor,
      directionLine[1][1] * scaleFactor,
      LINE_CROSSING_COLORS[LINE_CROSSING_STEPS.DIRECTION],
    );

    // Add point numbers
    ctx.fillStyle = LINE_CROSSING_COLORS[LINE_CROSSING_STEPS.DIRECTION];
    ctx.font = "bold 12px Arial";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("1", directionLine[0][0] * scaleFactor, directionLine[0][1] * scaleFactor - 12);
    ctx.fillText("2", directionLine[1][0] * scaleFactor, directionLine[1][1] * scaleFactor - 12);

    // Add direction label
    ctx.font = "12px Arial";
    ctx.textAlign = "left";
    ctx.fillText("Direction", directionLine[0][0] * scaleFactor + 10, directionLine[0][1] * scaleFactor - 5);
  } else if (directionLine.length === 1) {
    // Draw first point of direction line with number
    drawNode(
      ctx,
      directionLine[0][0] * scaleFactor,
      directionLine[0][1] * scaleFactor,
      LINE_CROSSING_COLORS[LINE_CROSSING_STEPS.DIRECTION],
    );

    // Add point number
    ctx.fillStyle = LINE_CROSSING_COLORS[LINE_CROSSING_STEPS.DIRECTION];
    ctx.font = "bold 12px Arial";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("1", directionLine[0][0] * scaleFactor, directionLine[0][1] * scaleFactor - 12);
  }

  // Draw work-in-progress crossing line
  if (crossingLine.length >= 1) {
    ctx.strokeStyle = LINE_CROSSING_COLORS[LINE_CROSSING_STEPS.CROSSING];

    if (crossingLine.length === 2) {
      // Draw completed crossing line
      ctx.beginPath();
      makeLine(
        ctx,
        crossingLine[0][0] * scaleFactor,
        crossingLine[0][1] * scaleFactor,
        crossingLine[1][0] * scaleFactor,
        crossingLine[1][1] * scaleFactor,
      );
      ctx.stroke();

      // Draw crossing line nodes with numbers
      drawNode(
        ctx,
        crossingLine[0][0] * scaleFactor,
        crossingLine[0][1] * scaleFactor,
        LINE_CROSSING_COLORS[LINE_CROSSING_STEPS.CROSSING],
      );
      drawNode(
        ctx,
        crossingLine[1][0] * scaleFactor,
        crossingLine[1][1] * scaleFactor,
        LINE_CROSSING_COLORS[LINE_CROSSING_STEPS.CROSSING],
      );

      // Add point numbers
      ctx.fillStyle = LINE_CROSSING_COLORS[LINE_CROSSING_STEPS.CROSSING];
      ctx.font = "bold 12px Arial";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("1", crossingLine[0][0] * scaleFactor, crossingLine[0][1] * scaleFactor - 12);
      ctx.fillText("2", crossingLine[1][0] * scaleFactor, crossingLine[1][1] * scaleFactor - 12);

      // Add crossing label
      ctx.font = "12px Arial";
      ctx.textAlign = "left";
      ctx.fillText("Crossing", crossingLine[0][0] * scaleFactor + 10, crossingLine[0][1] * scaleFactor - 5);
    } else if (crossingLine.length === 1) {
      // Draw first point of crossing line with number
      drawNode(
        ctx,
        crossingLine[0][0] * scaleFactor,
        crossingLine[0][1] * scaleFactor,
        LINE_CROSSING_COLORS[LINE_CROSSING_STEPS.CROSSING],
      );

      // Add point number
      ctx.fillStyle = LINE_CROSSING_COLORS[LINE_CROSSING_STEPS.CROSSING];
      ctx.font = "bold 12px Arial";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("1", crossingLine[0][0] * scaleFactor, crossingLine[0][1] * scaleFactor - 12);
    }
  }
}
